import { toCentimes } from "@efacture/fiscal-core";
import { SEARCH_INDEX_MODELS, normalizeSearchTerm, searchIndexFilter } from "./search-index.js";
/** Most lines one matching request may carry. */
export const CATALOGUE_MATCH_MAX_LINES = 200;
/** Most active articles one matching request scans. */
export const CATALOGUE_MATCH_MAX_ARTICLES = 10_000;
/** Most candidates returned for one line. */
export const CATALOGUE_MATCH_MAX_CANDIDATES = 5;
/** Lowest trigram similarity at which an article is proposed as approximate. */
export const APPROXIMATE_MATCH_THRESHOLD = 0.3;
/** What a learned alias was read as: the line's designation or its code. */
export const CATALOGUE_ALIAS_KINDS = ["DESIGNATION", "CODE"];
/**
 * The key an alias is stored and looked up under: accents and case folded
 * as the article search folds them, blanks trimmed and inner runs of blanks
 * reduced to one space. Null for an empty or blank text.
 */
export function normalizeCatalogueAliasKey(text) {
    const folded = normalizeSearchTerm(text ?? "").replace(/\s+/gu, " ").trim();
    return folded === "" ? null : folded;
}
/** The alias keys a read line is looked up under: its designation, then its code. */
export function catalogueAliasKeysOf(line) {
    const keys = [];
    const designation = normalizeCatalogueAliasKey(line.designation);
    if (designation)
        keys.push({ kind: "DESIGNATION", normalized: designation });
    const code = normalizeCatalogueAliasKey(line.code);
    if (code)
        keys.push({ kind: "CODE", normalized: code });
    return keys;
}
const ARTICLE_SEARCH_COLUMNS = (() => {
    const columns = SEARCH_INDEX_MODELS["Article"];
    if (!columns) {
        throw new Error("SEARCH_INDEX_MODELS.Article manquant : la recherche d'articles n'a pas de colonnes");
    }
    return columns;
})();
const WORD_SEPARATOR = /[^\p{L}\p{N}]+/u;
/**
 * Trigram set of a text: each folded word, padded as `  word `, cut into
 * 3-character slices. Any character that is neither a letter nor a digit
 * separates words, so word order and punctuation do not count.
 */
function trigrams(text) {
    const set = new Set();
    for (const word of normalizeSearchTerm(text).split(WORD_SEPARATOR)) {
        if (!word)
            continue;
        const padded = `  ${word} `;
        for (let i = 0; i + 3 <= padded.length; i += 1) {
            set.add(padded.slice(i, i + 3));
        }
    }
    return set;
}
/**
 * The trigrams of a text as ascending integer ids, numbered by `dictionary`,
 * which one comparison run shares so equal trigrams get equal ids.
 */
function trigramIds(text, dictionary) {
    const ids = [];
    for (const trigram of trigrams(text)) {
        let id = dictionary.get(trigram);
        if (id === undefined) {
            id = dictionary.size;
            dictionary.set(trigram, id);
        }
        ids.push(id);
    }
    return Uint32Array.from(ids).sort();
}
/** Shared ids over all distinct ids of two ascending id lists; 0 when either is empty. */
function jaccard(a, b) {
    if (a.length === 0 || b.length === 0)
        return 0;
    let shared = 0;
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
        const left = a[i];
        const right = b[j];
        if (left === right) {
            shared += 1;
            i += 1;
            j += 1;
        }
        else if (left < right) {
            i += 1;
        }
        else {
            j += 1;
        }
    }
    return shared / (a.length + b.length - shared);
}
/** Accent- and case-insensitive trigram similarity of two texts, from 0 to 1. */
export function trigramSimilarity(a, b) {
    const dictionary = new Map();
    return jaccard(trigramIds(a, dictionary), trigramIds(b, dictionary));
}
function searchBlobOf(article) {
    const values = ARTICLE_SEARCH_COLUMNS.map((column) => {
        const value = article[column];
        return typeof value === "string" ? value : "";
    });
    return normalizeSearchTerm(values.join(" "));
}
function toNumberOrNull(value) {
    return value === null || value === undefined ? null : Number(value);
}
const CONFIDENCE_BY_SOURCE = {
    BARCODE: "HIGH",
    ALIAS: "HIGH",
    EXACT_CODE: "HIGH",
    SEARCH: "MEDIUM",
    APPROXIMATE: "LOW",
};
function candidateOf(prepared, source, score, readUnitPrice) {
    const { article } = prepared;
    return {
        articleId: article.id,
        code: article.code,
        name: article.name,
        source,
        confidence: CONFIDENCE_BY_SOURCE[source],
        score,
        articleType: article.articleType,
        taxGroup: article.taxGroup,
        psvbGroup: article.psvbGroup,
        isBeverage: article.isBeverage,
        specificTaxMode: article.specificTaxMode ?? null,
        specificTaxRate: toNumberOrNull(article.specificTaxRate),
        specificTaxUnitAmount: toNumberOrNull(article.specificTaxUnitAmount),
        specificTaxLabel: article.specificTaxLabel ?? null,
        priceMode: article.priceMode,
        unitPrice: prepared.unitPrice,
        unit: article.unit,
        priceDiffers: readUnitPrice === null ? null : toCentimes(readUnitPrice) !== toCentimes(prepared.unitPrice),
    };
}
/** Best score first, then code, then id, so the order never depends on the catalogue order. */
function byRank(a, b) {
    if (a.score !== b.score)
        return b.score - a.score;
    const [left, right] = [a.entry.article, b.entry.article];
    if (left.code !== right.code)
        return left.code < right.code ? -1 : 1;
    if (left.id !== right.id)
        return left.id < right.id ? -1 : 1;
    return 0;
}
/**
 * Matches each read line against the active articles of `catalogue`, which
 * the caller loads for one tenant only.
 *
 * Candidates come in this order, each article at most once:
 *  - BARCODE (HIGH): the trimmed read barcode equals the article barcode;
 *  - ALIAS (HIGH): the folded designation or code of the line is a key in
 *    `aliases`, the tenant's confirmed aliases; designation first;
 *  - EXACT_CODE (HIGH): the trimmed read code equals the article code;
 *  - SEARCH (MEDIUM): the folded designation is contained in the folded
 *    code, name, description and category, as the article picker searches;
 *  - APPROXIMATE (LOW): trigram similarity of the designation to the
 *    article name at or above `APPROXIMATE_MATCH_THRESHOLD`.
 * Within the last three steps, candidates rank by similarity of the
 * designation to the name, then by code. An alias naming an article absent
 * from the active catalogue is ignored.
 *
 * A line with HIGH hits (barcode, alias, exact code) selects their article
 * when they all name one article, and nothing otherwise. A line without
 * HIGH hits selects an article only when exactly one article is found by
 * the search. An approximate candidate is never selected. Fiscal attributes
 * and the price always come from the article; the read price is only
 * compared with it.
 */
export function matchReadLines(lines, catalogue, aliases = []) {
    const dictionary = new Map();
    const prepared = catalogue
        .filter((article) => article.isActive)
        .map((article) => ({
        article,
        searchBlob: searchBlobOf(article),
        nameTrigrams: trigramIds(article.name, dictionary),
        unitPrice: Number(article.unitPrice),
    }));
    const byId = new Map(prepared.map((entry) => [entry.article.id, entry]));
    const aliasTargets = new Map();
    for (const alias of aliases) {
        aliasTargets.set(`${alias.kind}:${alias.normalized}`, alias.articleId);
    }
    const byBarcode = new Map();
    for (const entry of prepared) {
        const barcode = entry.article.barcode?.trim();
        if (barcode)
            byBarcode.set(barcode, entry);
    }
    const byCode = new Map();
    for (const entry of prepared) {
        const sameCode = byCode.get(entry.article.code);
        if (sameCode)
            sameCode.push(entry);
        else
            byCode.set(entry.article.code, [entry]);
    }
    return lines.map((line, index) => {
        const code = line.code?.trim() || null;
        const barcode = line.barcode?.trim() || null;
        const designation = line.designation?.trim() || null;
        const readUnitPrice = line.unitPrice ?? null;
        const scannedEntry = barcode === null ? undefined : byBarcode.get(barcode);
        const scanned = scannedEntry ? [{ entry: scannedEntry, score: 1 }] : [];
        const taken = new Set(scanned.map((hit) => hit.entry.article.id));
        const aliased = [];
        for (const key of catalogueAliasKeysOf(line)) {
            const articleId = aliasTargets.get(`${key.kind}:${key.normalized}`);
            const entry = articleId === undefined ? undefined : byId.get(articleId);
            if (entry && !taken.has(entry.article.id)) {
                aliased.push({ entry, score: 1 });
                taken.add(entry.article.id);
            }
        }
        const exact = (code ? byCode.get(code) ?? [] : [])
            .filter((entry) => !taken.has(entry.article.id))
            .map((entry) => ({ entry, score: 1 }));
        for (const hit of exact)
            taken.add(hit.entry.article.id);
        const search = [];
        const approximate = [];
        const needle = searchIndexFilter(designation)?.searchIndex.contains;
        if (needle) {
            const lineTrigrams = trigramIds(needle, dictionary);
            for (const entry of prepared) {
                if (taken.has(entry.article.id))
                    continue;
                const score = jaccard(lineTrigrams, entry.nameTrigrams);
                if (entry.searchBlob.includes(needle)) {
                    search.push({ entry, score });
                }
                else if (score >= APPROXIMATE_MATCH_THRESHOLD) {
                    approximate.push({ entry, score });
                }
            }
        }
        // Each article appears once across the HIGH steps, so one HIGH hit means
        // every HIGH step that matched names the same article.
        const high = [...scanned, ...aliased, ...exact];
        let selectedArticleId = null;
        if (high.length > 0) {
            if (high.length === 1)
                selectedArticleId = high[0]?.entry.article.id ?? null;
        }
        else if (search.length === 1) {
            selectedArticleId = search[0]?.entry.article.id ?? null;
        }
        const ranked = [
            ...scanned.map((hit) => ["BARCODE", hit]),
            ...aliased.map((hit) => ["ALIAS", hit]),
            ...exact.sort(byRank).map((hit) => ["EXACT_CODE", hit]),
            ...search.sort(byRank).map((hit) => ["SEARCH", hit]),
            ...approximate.sort(byRank).map((hit) => ["APPROXIMATE", hit]),
        ];
        return {
            index,
            designation,
            code,
            barcode,
            quantity: line.quantity,
            readUnitPrice,
            candidates: ranked
                .slice(0, CATALOGUE_MATCH_MAX_CANDIDATES)
                .map(([source, hit]) => candidateOf(hit.entry, source, hit.score, readUnitPrice)),
            selectedArticleId,
        };
    });
}
//# sourceMappingURL=catalogue-match.js.map